import {
  Box,
  BoxProps,
  Chip,
  chipClasses,
  IconButton,
  Modal,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { Gpexitthick, MoreCircle } from 'givepulse-iconsax-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 152,
    minWidth: '146px',
    gap: '10px',
  },

  tagsCausesContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
  },

  leftBottomContainer: {
    minWidth: 290,
    marginRight: '24px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  modalStyle: {
    position: 'absolute',
    //backgroundColor: 'red',
    borderRadius: '4px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    backgroundColor: theme.palette.background.paper,
    padding: 26,
    outline: 'none !important',
    [theme.breakpoints.down('sm')]: {
      width: 320,
    },
  },

  innerModalTopContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },

  modalTagCauseBox: {
    display: 'flex',
    padding: '4px',
    borderRadius: '4px',
    minWidth: '100%',
    minHeight: 'inherit',
    border: '1px solid #D4D4D4',
  },

  closeModalButton: {
    display: 'flex',
    marginBottom: '16px',
    marginLeft: 'auto',
    cursor: 'pointer',
    '&:hover': {
      color: '#3c52b2',
    },
  },

  locationText: {
    color: theme.palette.text.primary,
  },

  tagChip: {
    height: '24px',
    color: theme.palette.text.secondary,
  },
}));

type tagProps = {
  unique_id: number | string;
  title: string;
  name?: string;
};

type ListItemTagBarProps = {
  tags?: Partial<tagProps>[];
  rootSx?: BoxProps['sx'];
};

const ListItemTagBar = ({ tags = [], rootSx = {} }: ListItemTagBarProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const theme = useTheme();

  const TagsChip = styled(Chip)({
    maxWidth: '140px',
    [`&.${chipClasses.colorPrimary}`]: {
      color: theme.palette.text.secondary,
    },
    [`&.${chipClasses.outlined}.${chipClasses.colorPrimary}`]: {
      color: theme.palette.text.secondary,
      border: `solid 1px ${theme.palette.text.secondary}`,
    },
  });

  const lgDown = useMediaQuery<Theme>(theme => theme.breakpoints.down('lg'));

  const howManyTags = lgDown ? 2 : 3;

  const allTagsModalTrigger =
    tags.length > howManyTags ? (
      <Box sx={{ border: 'none' }}>
        <IconButton
          component="div" // bypass hydration bad DOM nesting error (parent GPLink `a` > `button`)
          onClick={e => {
            e.preventDefault(); // for parent GPLink
            e.stopPropagation(); // for parent GPLink
            handleOpen();
          }}
          aria-label={t('See All')}
        >
          <MoreCircle />
        </IconButton>
        <Modal
          open={open}
          onClose={(e: React.SyntheticEvent) => {
            e.preventDefault(); // for parent GPLink
            e.stopPropagation(); // for parent GPLink
            handleClose();
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            className={classes.modalStyle}
            onClick={e => {
              e.preventDefault(); // for parent GPLink
              e.stopPropagation(); // for parent GPLink
            }}
          >
            <Box className={classes.innerModalTopContainer}>
              <Typography
                sx={{ marginTop: '0', marginBottom: '19px' }}
                variant="subtitle1"
                component="h2"
              >
                {tags.some(tag => 'name' in tag) ? t('Registration Tags') : t('Cause_other')}
              </Typography>
              <Box className={classes.closeModalButton} onClick={handleClose}>
                <Gpexitthick size={18} />
              </Box>
            </Box>
            <Box className={classes.modalTagCauseBox}>
              <Box
                sx={{
                  padding: '8px',
                  display: 'inline-flex',
                  gap: '6px',
                  flexWrap: 'wrap',
                }}
              >
                {tags.map(tag => (
                  <TagsChip
                    sx={{ maxWidth: '100% !important' }}
                    label={tag.name ?? tag.title}
                    variant="outlined"
                    color="primary"
                    key={tag.unique_id}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    ) : null;

  return (
    <Box sx={{ display: 'flex', gap: '4px', ...rootSx }}>
      <Box className={classes.tagsCausesContainer}>
        {tags.slice(0, howManyTags).map((tag, i) => (
          <Tooltip title={tag.name ?? tag.title} key={tag.unique_id ?? i}>
            <TagsChip
              className={classes.tagChip}
              label={<Typography noWrap>{tag.name ?? tag.title}</Typography>}
              variant="outlined"
              color="primary"
            />
          </Tooltip>
        ))}
        {allTagsModalTrigger}
      </Box>
    </Box>
  );
};

export default ListItemTagBar;
